/**
 * Signals that an interactive auth redirect has been started and the browser is navigating
 * away — Bug #14687.
 *
 * `useAuth.getAccessToken` used to `return ''` after calling `acquireTokenRedirect(...)`, which the
 * caller could not distinguish from a real token — so it fetched with an empty bearer and 401'd.
 * On the admin portal that additionally triggered a logout on top of the in-flight redirect (two
 * competing navigations, which broke sign-in). This portal has no such logout path, so the symptom
 * was milder — a silent failed request — but the cause is identical and worth fixing once.
 *
 * Throwing makes "I am navigating away" impossible to mistake for a token.
 */
export class RedirectInProgressError extends Error {
  constructor(message = 'Interactive authentication redirect in progress') {
    super(message);
    this.name = 'RedirectInProgressError';
  }
}

/** True when `error` is the navigating-away sentinel rather than a real failure. */
export function isRedirectInProgress(error: unknown): error is RedirectInProgressError {
  return error instanceof RedirectInProgressError;
}

/**
 * Whether MSAL currently has an interaction underway, from msal-react's `inProgress`.
 *
 * <b>Deliberately fails OPEN on an unknown value.</b> `inProgress` is typed as
 * `InteractionStatus` and msal-react always supplies it, so `undefined` should be impossible — but
 * a naive `inProgress !== InteractionStatus.None` treats `undefined` as "interaction in progress"
 * and would then block sign-in *forever*, not merely after a timeout. That is a strictly worse
 * failure than the double-navigation this guard exists to prevent, so an unknown value is read as
 * "no interaction" and the caller proceeds.
 */
export function isInteractionInProgress(inProgress: string | undefined): boolean {
  return inProgress !== undefined && inProgress !== 'none';
}
